<template>
    <v-container class="speakers-list-container py-5">
        <v-row class="mb-5 align-center justify-space-between">
            <v-col cols="12" md="8">
                <h1 class="text-h4 font-weight-bold text-gradient mb-2">
                    Nossos Palestrantes
                </h1>
                <p class="text-subtitle-1 text-grey-darken-1">
                    Conheça os especialistas e palestrantes convidados do evento.
                </p>
            </v-col>
            <v-col cols="12" md="4" class="text-md-right text-left mt-3 mt-md-0">
                <a
                    href="/speakers/register"
                    class="btn btn-primary px-4 py-2 text-white"
                    style="border-radius: 30px; font-weight: 600;"
                >
                    <i class="fas fa-user-plus mr-2"></i>
                    Quero ser Palestrante
                </a>
            </v-col>
        </v-row>

        <v-divider class="mb-5"></v-divider>

        <v-row v-if="loading" justify="center" align="center" class="py-5">
            <v-progress-circular
                indeterminate
                color="primary"
                size="64"
            ></v-progress-circular>
        </v-row>

        <v-row v-else-if="speakers.length === 0" justify="center" class="py-5">
            <v-col cols="12" md="6" class="text-center">
                <i class="fas fa-user-slash fa-4x text-muted mb-4"></i>
                <h3 class="text-h5 text-grey-darken-2 mb-2">Nenhum palestrante cadastrado</h3>
                <p class="text-grey">Seja o primeiro a se inscrever como palestrante no nosso evento!</p>
            </v-col>
        </v-row>

        <v-row v-else>
            <v-col
                v-for="speaker in sortedSpeakers"
                :key="speaker.id"
                cols="12"
                sm="6"
                md="4"
                class="pa-3"
            >
                <v-card
                    class="speaker-list-card elevation-1 cursor-pointer"
                    @click="openSpeakerModal(speaker)"
                >
                    <div class="speaker-avatar-wrap mb-3">
                        <v-avatar size="96" class="profile-avatar-card">
                            <img
                                v-if="speaker.profile && speaker.profile.picture"
                                :src="speaker.profile.picture"
                                :alt="speaker.name"
                                class="avatar-img"
                            />
                            <i class="fas fa-user fa-2x text-primary" v-else></i>
                        </v-avatar>
                    </div>

                    <h3 class="text-h6 font-weight-bold text-grey-darken-3 text-center mb-2 speaker-card-name">
                        {{ speaker.name }}
                    </h3>

                    <p
                        class="text-body-2 text-grey-darken-2 speaker-card-description mb-3"
                        v-if="speaker.profile && speaker.profile.credentials"
                    >
                        {{ speaker.profile.credentials }}
                    </p>

                    <v-divider class="mb-3"></v-divider>

                    <div class="speaker-card-details text-grey-darken-2">
                        <div class="mb-3" v-if="speaker.profile && speaker.profile.themes">
                            <div class="text-caption font-weight-bold text-grey-darken-1 text-uppercase mb-1">
                                Temas de Interesse
                            </div>
                            <div class="text-body-2 speaker-card-themes">
                                {{ speaker.profile.themes }}
                            </div>
                        </div>

                    </div>

                    <div class="speaker-card-buttons" v-if="hasFooterButtons(speaker)">
                        <a
                            v-if="speaker.profile.lattes"
                            :href="speaker.profile.lattes"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="portfolio-btn"
                            @click.stop
                        >
                            <i class="fas fa-external-link-alt mr-2"></i>
                            <span class="portfolio-btn-label">Acessar Portfólio / Currículo</span>
                        </a>
                        <a
                            v-for="net in socialLinks(speaker)"
                            :key="net.key"
                            :href="net.href"
                            target="_blank"
                            rel="noopener noreferrer"
                            :class="['linkedin-btn', 'social-btn--' + net.key]"
                            :title="net.label"
                            @click.stop
                        >
                            <i :class="net.icon"></i>
                        </a>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="dialog" max-width="500px" scrollable>
            <v-card v-if="selectedSpeaker" class="d-flex flex-column rounded-card">
                <v-card-title class="d-flex align-center pb-2 px-3 pt-3" style="border-bottom: 1px solid rgba(0,0,0,0.08);">
                    <v-avatar size="52" class="mr-3 profile-avatar-modal">
                        <img
                            v-if="selectedSpeaker.profile && selectedSpeaker.profile.picture"
                            :src="selectedSpeaker.profile.picture"
                            :alt="selectedSpeaker.name"
                        />
                        <i class="fas fa-user fa-lg text-primary" v-else></i>
                    </v-avatar>
                    <div>
                        <div class="font-weight-bold speaker-modal-name">{{ selectedSpeaker.name }}</div>
                    </div>
                </v-card-title>

                <v-card-text class="pt-3 px-3 modal-body-content" style="max-height: 55vh; overflow-y: auto;">
                    <div v-if="selectedSpeaker.profile && selectedSpeaker.profile.credentials" class="mb-4">
                        <div class="font-weight-bold modal-section-title text-grey-darken-3 mb-1">
                            <i class="fas fa-user mr-2 text-primary"></i> Sobre
                        </div>
                        <div class="theme-text pl-6 modal-section-text text-grey-darken-2">
                            {{ selectedSpeaker.profile.credentials }}
                        </div>
                    </div>

                    <div v-if="selectedSpeaker.profile && selectedSpeaker.profile.themes" class="mb-4">
                        <div class="font-weight-bold modal-section-title text-grey-darken-3 mb-1">
                            <i class="fas fa-bullhorn mr-2 text-primary"></i> Temas de Interesse
                        </div>
                        <div class="theme-text pl-6 modal-section-text text-grey-darken-2">
                            {{ selectedSpeaker.profile.themes }}
                        </div>
                    </div>

                    <div v-if="selectedSpeaker.profile && (socialLinks(selectedSpeaker).length || selectedSpeaker.profile.lattes)" class="mb-2">
                        <div class="font-weight-bold modal-section-title text-grey-darken-3 mb-2">
                            <i class="fas fa-link mr-2 text-primary"></i> Links Profissionais
                        </div>
                        <div class="pl-6 d-flex flex-column" style="gap: 8px;">
                            <a
                                v-for="net in socialLinks(selectedSpeaker)"
                                :key="net.key"
                                :href="net.href"
                                target="_blank"
                                rel="noopener"
                                class="social-link-btn d-inline-flex align-center"
                            >
                                <i :class="[net.icon, 'fa-lg mr-2 text-info']"></i> {{ net.label }}
                            </a>
                            <a
                                v-if="selectedSpeaker.profile.lattes"
                                :href="selectedSpeaker.profile.lattes"
                                target="_blank"
                                rel="noopener"
                                class="social-link-btn d-inline-flex align-center"
                            >
                                <i class="fas fa-graduation-cap fa-lg mr-2 text-success"></i> Portfólio / Currículo
                            </a>
                        </div>
                    </div>
                </v-card-text>

                <v-card-actions class="justify-end pt-2 px-3 pb-3" style="border-top: 1px solid rgba(0,0,0,0.08);">
                    <v-btn
                        variant="text"
                        @click="dialog = false"
                        class="font-weight-bold"
                        style="font-size: 0.85rem; color: var(--sys-button) !important;"
                    >
                        Fechar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
export default {
    name: 'SpeakersList',
    data() {
        return {
            speakers: [],
            loading: true,
            dialog: false,
            selectedSpeaker: null
        };
    },
    computed: {
        sortedSpeakers() {
            return [...this.speakers].sort((a, b) => {
                return (a.name || '').localeCompare(b.name || '', 'pt-BR', { sensitivity: 'base' });
            });
        }
    },
    created() {
        this.fetchSpeakers();
    },
    methods: {
        openSpeakerModal(speaker) {
            this.selectedSpeaker = speaker;
            this.dialog = true;
        },
        /**
         * Define se o card exibe a linha de botões do rodapé
         * (portfólio/currículo e/ou redes sociais preenchidas). Sem nenhum,
         * a linha não é renderizada e o card não ganha espaço vazio.
         */
        hasFooterButtons(speaker) {
            const profile = speaker.profile;
            return !!(profile && (profile.lattes || this.socialLinks(speaker).length));
        },
        /**
         * Redes sociais preenchidas no perfil do palestrante, na ordem de
         * exibição (LinkedIn, Instagram, Facebook, Twitter/X). Campo vazio
         * não gera botão. O link ganha "https://" quando foi cadastrado sem
         * protocolo (ex.: "instagram.com/fulano"), senão viraria rota do site.
         *
         * @param {Object} speaker
         * @returns {Array<{key: string, label: string, icon: string, href: string}>}
         */
        socialLinks(speaker) {
            const profile = (speaker && speaker.profile) || {};
            return [
                { key: 'linkedin', label: 'LinkedIn', icon: 'fab fa-linkedin-in' },
                { key: 'instagram', label: 'Instagram', icon: 'fab fa-instagram' },
                { key: 'facebook', label: 'Facebook', icon: 'fab fa-facebook-f' },
                { key: 'twitter', label: 'Twitter / X', icon: 'fab fa-twitter' },
            ]
                .filter((net) => profile[net.key] && String(profile[net.key]).trim())
                .map((net) => ({ ...net, href: this.externalUrl(profile[net.key]) }));
        },
        /** Garante protocolo no link externo (sem isso o navegador trata "instagram.com/x" como caminho do site). */
        externalUrl(url) {
            const value = String(url).trim();
            return /^https?:\/\//i.test(value) ? value : `https://${value}`;
        },
        fetchSpeakers() {
            this.loading = true;
            window.axios.get('/api/public/speakers/list')
                .then(response => {
                    this.speakers = response.data;
                })
                .catch(error => {
                    console.error('Erro ao buscar palestrantes:', error);
                    this.$store.commit('SET_NOTIFICATION', {
                        message: 'Erro ao carregar a lista de palestrantes.',
                        status: 'error'
                    });
                })
                .finally(() => {
                    this.loading = false;
                });
        }
    }
};
</script>

<style scoped>
.speakers-list-container {
    max-width: 1200px;
    margin: 0 auto;
}

.text-gradient {
    background: var(--sys-secondary, #1976D2);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
}

.speaker-list-card {
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    height: 100%;
    /* vertical igual ao pa-4 anterior; 36px nas laterais para o
       conteúdo (bio, temas e botões) não encostar nas bordas */
    padding: 16px 36px;
}

.speaker-list-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08) !important;
}

/* A página legada (/speakers) não carrega as variáveis de tema do Vuetify 3;
   o calc(var(--v-*-opacity)) do overlay vira opacity:1 e escurece o card
   inteiro após clique/foco. O hover próprio acima já dá o feedback visual. */
.speaker-list-card :deep(.v-card__overlay) {
    display: none;
}

.speaker-avatar-wrap {
    display: flex;
    justify-content: center;
}

.profile-avatar-card {
    flex-shrink: 0;
    border: 3px solid #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    background: #f1f5f9;
}

.avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.speaker-card-name {
    line-height: 1.3;
    word-break: break-word;
}

.speaker-card-description {
    line-height: 1.5;
    word-break: break-word;
    display: -webkit-box;
    -webkit-line-clamp: 5;
    line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.speaker-card-details {
    word-break: break-word;
}

.speaker-card-themes {
    line-height: 1.5;
}

/* linha de botões no rodapé do card: portfólio (sólido, cor do tema)
   ocupa a largura e o LinkedIn é um quadrado de 42px ao lado */
.speaker-card-buttons {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: auto; /* empurra para o rodapé, mantendo os cards alinhados */
    padding-top: 16px;
}

.portfolio-btn {
    flex: 1 1 auto;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 42px;
    padding: 0 14px;
    border-radius: 10px;
    /* mesma dupla de cores dos botões/menu da plataforma
       (fundo primária + texto secundária), nunca cor fixa */
    background-color: var(--sys-primary, #5e72e4);
    color: var(--sys-secondary, #ffffff);
    font-weight: 600;
    font-size: 0.85rem;
    text-decoration: none;
    transition: filter 0.2s;
}

.portfolio-btn:hover {
    filter: brightness(0.92);
    color: var(--sys-secondary, #ffffff);
}

.portfolio-btn-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.linkedin-btn {
    flex: 0 0 42px;
    width: 42px;
    height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #d1d5db;
    color: #0A66C2; /* azul oficial do LinkedIn */
    font-size: 1.15rem;
    text-decoration: none;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.linkedin-btn:hover {
    background: #0A66C2;
    border-color: #0A66C2;
    color: #ffffff;
}

/* mesmas medidas/comportamento do botão do LinkedIn, só muda a cor oficial da rede */
.social-btn--instagram { color: #E1306C; }
.social-btn--instagram:hover { background: #E1306C; border-color: #E1306C; color: #ffffff; }
.social-btn--facebook { color: #1877F2; }
.social-btn--facebook:hover { background: #1877F2; border-color: #1877F2; color: #ffffff; }
.social-btn--twitter { color: #1DA1F2; }
.social-btn--twitter:hover { background: #1DA1F2; border-color: #1DA1F2; color: #ffffff; }

.cursor-pointer {
    cursor: pointer;
}

.rounded-card {
    border-radius: 16px !important;
}

.profile-avatar-modal {
    border: 3px solid #ffffff;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
}

.theme-text {
    font-size: 0.9rem;
    line-height: 1.5;
    white-space: normal;
    word-break: break-word;
}

.social-link-btn {
    text-decoration: none;
    color: #4a5568;
    font-size: 0.9rem;
    font-weight: 500;
    transition: color 0.2s;
}

.social-link-btn:hover {
    color: #1a202c;
}

.speaker-modal-name {
    font-size: 1.1rem !important;
    line-height: 1.2 !important;
}

.modal-section-title {
    font-size: 0.9rem !important;
    font-weight: 700 !important;
}

.modal-section-text {
    font-size: 0.85rem !important;
    line-height: 1.4 !important;
}

.modal-body-content {
    max-height: 55vh;
    overflow-y: auto;
}
</style>
